import React, { useEffect, useState } from "react";
import {
  DialogTitle,
  DialogContent,
  Typography,
  TextField,
  Button,
  Box,
  IconButton,
  makeStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import ProgressSteps from "./progressSteps";
import CCBillPayment from "./CCBillPayment";
import { useAppSelector } from "../../redux/typedHooks";
import { UserInterface } from "../../types/user";
import { SubscriptionTier } from "../Header/ProfileViewer/profile-viewer";
import { getUserProfile } from "../../api/userApi";

interface ProfileData {
  _id: string;
  firstName: string;
  lastName: string;
  username: string;
  profileImg: string;
  validated: boolean;
  postsCount: number;
  followersCount: number;
  description: string;
  website: string;
  email: string;
  private: boolean;
  subscriptionPrice: SubscriptionTier[];
  follow: {
    followUserId: string;
    accepted: boolean;
  } | null;
  link?: string;
  suggestedTips?: number[];
  minimumTipAmount?: number;
  customContentPrice?: number;
}

interface Props {
  onClose: () => void;
  onSubmit: (data: any) => void;
  price?: number;
  modelId?: string;
  conversationId?: string; // For sending video request to conversation
  recipientId?: string; // Creator ID for the request
}

const useStyles = makeStyles(theme => ({
  modalContainer: {
    borderRadius: "16px",
    padding: "40px",
    [theme.breakpoints.down("sm")]: {
      padding: "32px 16px",
    }
  },
  dialogTitle: {
    fontWeight: 700, fontSize: 32,
    [theme.breakpoints.down("sm")]: {
      fontSize: 20,
    },
  },
  inputDescription: {
    backgroundColor: "#1F1F1F",
    color: "#fff",
    padding: 16,
    fontSize: 20,
    border: "1px solid #747474",
    borderRadius: 8,
    [theme.breakpoints.down("sm")]: {
      fontSize: 16,
    },
  },
  buttonContainer: {
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      gap: 24
    },
  },
  priceContainer: {
    display: "flex",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      justifyContent: "space-between"
    },
  },
  proceedButton: {
    color: "#fff",
    textTransform: "none",
    fontSize: 20,
    padding: "12px 40px",
    borderRadius: 6,
    [theme.breakpoints.down("sm")]: {
      width: "100%"
    },
  }
}))

export const CustomVideoRequestDialog: React.FC<Props> = ({
  onClose,
  onSubmit,
  price = 100,
  modelId,
  conversationId,
  recipientId,
}) => {
  const [description, setDescription] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [sliderValue, setSliderValue] = useState(0);
  const [profile, setProfile] = useState<ProfileData | null>(null);
  // const { id } = useParams<{ id?: string }>();
  const me = useAppSelector<UserInterface | null>(
    state => state.user.currentUser
  );
  const [, setLoading] = useState(true);
  //const loadId = useMemo(() => id || me?.id, [id, me?.id]);
  const loadId = modelId || me?.id;
  const classes = useStyles();


  // Validation function for description
  const validateDescription = (value: string) => {
    if (!value.trim()) {
      return "Description is required";
    }
    if (value.trim().length < 20) {
      return "Description must be at least 20 characters long";
    }
    return "";
  };

  // Handle description change with validation
  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDescription(value);
    setDescriptionError(validateDescription(value));
  };

  useEffect(() => {
    if (!loadId) {
      setLoading(false);
      return;
    }
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const data = await getUserProfile(loadId);
        const subscriptionPrice =
          Array.isArray(data.subscriptionPrice) && data.subscriptionPrice.length
            ? data.subscriptionPrice
            : [{ tierName: "Premium", price: 50, durationInDays: 30 }];
        const suggestedTips =
          Array.isArray(data.suggestedTips) && data.suggestedTips.length
            ? data.suggestedTips
            : [5, 10, 15, 20, 25];
        setProfile({
          _id: data._id,
          firstName: data.firstName,
          lastName: data.lastName,
          username: data.username,
          profileImg: data.profileImg,
          validated: data.validated,
          postsCount: data.postsCount,
          followersCount: data.followersCount,
          description: data.bio,
          website: data.website,
          follow: data.follow,
          link: data.link,
          email: data.email,
          private: data.private,
          subscriptionPrice,
          suggestedTips,
          minimumTipAmount: data.minimumTipAmount || 2,
          customContentPrice: data.customContentPrice || 100,
        });
      } catch (err) {
        const error = err as Error;
        console.error("Error loading profile:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [loadId, me?.id]);

  // Check for payment success on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentSuccess = urlParams.get("payment_success");
    const context = urlParams.get("context");

    if (paymentSuccess === "true" && context === "custom-video") {
      // Validate description before proceeding
      const validationError = validateDescription(description);
      if (validationError) {
        setDescriptionError(validationError);
        return;
      }

      // Payment was successful, trigger the video request creation
      const videoRequestData = {
        description,
        price,
        creatorId: profile?._id, // Changed from modelId to creatorId
        conversationId,
        recipientId,
        estimatedDuration: 5, // Default 5 minutes
        specialInstructions: "",
        tags: [],
      };

      onSubmit(videoRequestData);
      setSliderValue(2);

      // Clean up URL parameters
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [
    description,
    price,
    modelId,
    profile,
    conversationId,
    recipientId,
    onSubmit,
  ]);

  const handleSubmit = () => {
    if (sliderValue >= 2) {
      onSubmit({
        description: description,
        price: price,
      });
      return;
    }

    if (sliderValue === 0) {
      setSliderValue(1);
      return;
    }
    if (sliderValue === 1) {
      setSliderValue(2);
      return;
    }
  };

  return (
    <Box
      bgcolor="#131313"
      color="#fff"
      padding={2}
      position="relative"
      className={classes.modalContainer}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        position="relative"
        style={{ paddingRight: "40px" }}
      >
        <DialogTitle
          style={{
            textAlign: "left",
            color: "white",
            padding: 0,
            margin: 0,
            flex: 1,
          }}
        >
          <Typography variant="h6" className={classes.dialogTitle}>
            {sliderValue !== 2
              ? "Request video"
              : "Request Submitted! 🎉"}
          </Typography>
        </DialogTitle>
        <IconButton
          onClick={onClose}
          style={{
            position: "absolute",
            right: 0,
            top: "50%",
            transform: "translateY(-50%)",
            color: "#fff",
            padding: 0
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent style={{ padding: "32px 0" }}>
        {/* <Box display="flex" justifyContent="center" mb={3}>
          <ProgressSteps currentStep={sliderValue} totalSteps={3} />
        </Box> */}

        {sliderValue === 2 && (
          <>
            {/* <Typography align="center" style={{ marginBottom: 24 }}>
              Your custom video request has been sent to the creator. You’ll be
              notified once it’s completed.
            </Typography> */}

            <Typography
              align="center"
              style={{
                color: "#ff69b4",
                fontWeight: 500,
                marginBottom: 16,
              }}
            >
              Your Request
            </Typography>

            <Box
              style={{
                backgroundColor: "#000",
                padding: 16,
                borderRadius: 8,
                marginBottom: 24,
              }}
            >
              <Typography
                align="center"
                style={{
                  fontStyle: "italic",
                  color: "#fff",
                }}
              >
                “{description || "No description provided."}”
              </Typography>
            </Box>
          </>
        )}

        {sliderValue === 1 && (
          <CCBillPayment
            price={price}
            context="custom-video"
            title="Custom Video Payment"
            buttonText="Pay Securely with CCBill"
            description={description} // ← ОБОВ'ЯЗКОВО!
            userId={me?.id} // Той хто платить
            modelId={profile?._id} // Той кому платять
            email={me?.email}
            tipForUserId={profile?._id}
            onProceed={() => {
              // This is called when user clicks the payment button
              // The actual success handling is in onPaymentSuccess
            }}
            onPaymentSuccess={async () => {
              // Validate description before proceeding
              const validationError = validateDescription(description);
              if (validationError) {
                setDescriptionError(validationError);
                return;
              }

              // This is called when payment is actually successful
              const videoRequestData = {
                description,
                price,
                creatorId: profile?._id, // Changed from modelId to creatorId
                conversationId,
                recipientId,
                estimatedDuration: 5, // Default 5 minutes
                specialInstructions: "",
                tags: [],
              };

              // Call the onSubmit callback with the video request data
              await onSubmit(videoRequestData);

              // Move to success step
              setSliderValue(2);
            }}
          />
        )}

        {sliderValue === 0 && (
          <>
            {/* <Typography align="center" style={{ marginBottom: 2 }}>
              Want a personalized video from your favorite creator? Submit your
              request now!
            </Typography> */}

            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Tell creator more about your request and any preferences (min. 20 characters)"
              variant="outlined"
              value={description}
              onChange={handleDescriptionChange}
              error={!!descriptionError}
              helperText={
                descriptionError ||
                `${description.length}/200 characters`
              }
              InputProps={{
                className: classes.inputDescription
                // style: { backgroundColor: "#1F1F1F", color: "#fff", padding: 16, fontSize: 20, border: "1px solid #747474", borderRadius: 8 },
              }}
              FormHelperTextProps={{
                style: {
                  color: descriptionError ? "#f44336" : "#FFFFFF",
                  fontSize: "14px",
                  marginLeft: 0,
                  marginTop: "4px",
                  marginBottom: "12px",
                },
              }}
            />

            <Typography
              align="center"
              style={{
                marginBottom: 2,
                color: "#FFFFFFCC",
                fontSize: "14px",
                textAlign: "left",
                lineHeight: "110%"
              }}
            >
              If the creator accepts your request, you’ll receive your video{" "}
              <Typography component="span" style={{ fontWeight: 600, fontSize: "14px", }}>
                within 7 business days
              </Typography>
              . Otherwise, your payment will be automatically refunded.
            </Typography>
          </>
        )}
      </DialogContent>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        pt={2}
        borderTop="1px solid #1F1F1F"
        className={classes.buttonContainer}
      >
        <Box className={classes.priceContainer}>
          <div
            style={{
              textDecoration: sliderValue === 2 ? "line-through" : "none",
              fontWeight: 400,
              marginRight: 12
            }}
          >
            Price:
          </div>{" "}
          <div
            style={{
              color: "#ff69b4",
              textDecoration: sliderValue === 2 ? "line-through" : "none",
            }}
          >
            ${price}
          </div>
          {sliderValue === 2 && (
            <span style={{ color: "#31D03F", fontWeight: "bold" }}> PAID</span>
          )}
        </Box>

        <Button
          variant="contained"
          disabled={
            sliderValue === 0 &&
            (!!descriptionError || description.trim().length < 20)
          }
          style={{
            backgroundColor:
              sliderValue === 0 &&
                (!!descriptionError || description.trim().length < 20)
                ? "#666"
                : "#FE5A97",
            display: sliderValue === 1 ? "none" : "block", // Hide button on payment step
          }}
          className={classes.proceedButton}
          onMouseOver={(e: React.MouseEvent<HTMLButtonElement>) => {
            if (
              !(
                sliderValue === 0 &&
                (!!descriptionError || description.trim().length < 20)
              )
            ) {
              e.currentTarget.style.backgroundColor = "#FE5A97";
            }
          }}
          onMouseOut={(e: React.MouseEvent<HTMLButtonElement>) => {
            if (
              !(
                sliderValue === 0 &&
                (!!descriptionError || description.trim().length < 20)
              )
            ) {
              e.currentTarget.style.backgroundColor = "#ff4da6";
            }
          }}
          onClick={handleSubmit}
        >
          {sliderValue === 0 && "Proceed payment"}
          {sliderValue === 2 && "Done"}
        </Button>
      </Box>
    </Box>
  );
};
